import React, { useState } from "react";
import { Users, CreditCard, DollarSign, UserCheck, AlertTriangle, TrendingUp, Eye } from "lucide-react";
import { Sidebar } from "../components/navigation/features/SideBar";
import { Header } from "../components/navigation/features/Header";
import ProtectedRoute from "../../../wrappers/ProtectedRoutes";

// Types
interface LayoutProps {
  children: React.ReactNode;
}

// Layout Component
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isCollapsed={sidebarCollapsed} setIsCollapsed={setSidebarCollapsed} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

        <main className="flex-1 overflow-y-auto p-4">{children}</main>
      </div>
    </div>
  );
};

// Dashboard Components
const StatCard: React.FC<{
  title: string;
  value: string;
  change: string;
  icon: React.ElementType;
  trend: "up" | "down";
}> = ({ title, value, change, icon: Icon, trend }) => {
  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
          <p className={`text-sm ${trend === "up" ? "text-green-600" : "text-red-600"} flex items-center mt-1`}>
            <TrendingUp className={`w-4 h-4 mr-1 ${trend === "down" ? "rotate-180" : ""}`} />
            {change}
          </p>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <Icon className="w-6 h-6 text-green-600" />
        </div>
      </div>
    </div>
  );
};

const RecentTransactions: React.FC = () => {
  const transactions = [
    {
      id: "TXN001",
      sender: "Jean Dupont",
      receiver: "Marie Martin",
      amount: "250 CAD",
      status: "completed",
      country: "🇨🇦→🇨🇲",
    },
    {
      id: "TXN002",
      sender: "Paul Kamga",
      receiver: "Sophie Benin",
      amount: "120 CAD",
      status: "pending",
      country: "🇨🇦→🇧🇯",
    },
    {
      id: "TXN003",
      sender: "Alice Johnson",
      receiver: "David Yao",
      amount: "300 CAD",
      status: "completed",
      country: "🇨🇦→🇹🇬",
    },
    {
      id: "TXN004",
      sender: "Michel Toto",
      receiver: "Grace Nana",
      amount: "180 CAD",
      status: "failed",
      country: "🇨🇦→🇨🇲",
    },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Transactions Récentes</h3>
          <button className="text-green-600 hover:text-green-700 text-sm font-medium">Voir tout</button>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {transactions.map((tx) => (
            <div key={tx.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">
                    {tx.sender} → {tx.receiver}
                  </p>
                  <p className="text-sm text-gray-600">
                    {tx.id} • {tx.country}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-800">{tx.amount}</p>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    tx.status === "completed"
                      ? "bg-green-100 text-green-700"
                      : tx.status === "pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-red-100 text-red-700"
                  }`}
                >
                  {tx.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PendingKYC: React.FC = () => {
  const pendingUsers = [
    { name: "Ahmed Hassan", country: "🇲🇦", submitted: "2h ago", risk: "low" },
    { name: "Fatou Diallo", country: "🇸🇳", submitted: "4h ago", risk: "medium" },
    { name: "John Smith", country: "🇨🇦", submitted: "1d ago", risk: "high" },
    { name: "Amina Kone", country: "🇨🇮", submitted: "2d ago", risk: "low" },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">KYC en Attente</h3>
          <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm font-medium">
            {pendingUsers.length} en attente
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {pendingUsers.map((user, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">{user.name}</p>
                  <p className="text-sm text-gray-600">
                    {user.country} • {user.submitted}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    user.risk === "low"
                      ? "bg-green-100 text-green-700"
                      : user.risk === "medium"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-red-100 text-red-700"
                  }`}
                >
                  {user.risk} risk
                </span>
                <button className="p-1 text-gray-400 hover:text-gray-600">
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AlertsWidget: React.FC = () => {
  const alerts = [
    {
      type: "fraud",
      message: "Transaction suspecte détectée",
      user: "USER_12345",
      time: "5 min ago",
      severity: "high",
    },
    { type: "kyc", message: "Document expiré", user: "USER_67890", time: "15 min ago", severity: "medium" },
    { type: "system", message: "API Interac lente", time: "1h ago", severity: "low" },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Alertes Actives</h3>
          <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-sm font-medium">
            {alerts.filter((a) => a.severity === "high").length} critiques
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {alerts.map((alert, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    alert.severity === "high"
                      ? "bg-red-100"
                      : alert.severity === "medium"
                        ? "bg-yellow-100"
                        : "bg-blue-100"
                  }`}
                >
                  <AlertTriangle
                    className={`w-5 h-5 ${
                      alert.severity === "high"
                        ? "text-red-600"
                        : alert.severity === "medium"
                          ? "text-yellow-600"
                          : "text-blue-600"
                    }`}
                  />
                </div>
                <div>
                  <p className="font-medium text-gray-800">{alert.message}</p>
                  <p className="text-sm text-gray-600">
                    {alert.user || "Système"} • {alert.time}
                  </p>
                </div>
              </div>
              <button className="text-green-600 hover:text-green-700 text-sm font-medium">Traiter</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Dashboard Page Content
const DashboardContent: React.FC = () => {
  return (
    <>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Dashboard</h1>
        <p className="text-gray-600">Aperçu général de votre plateforme Izi Life Pay</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="Transactions Aujourd'hui" value="1,247" change="+12.5%" icon={CreditCard} trend="up" />
        <StatCard title="Utilisateurs Actifs" value="8,542" change="+8.2%" icon={Users} trend="up" />
        <StatCard title="Revenus (24h)" value="$12,450" change="+15.8%" icon={DollarSign} trend="up" />
        <StatCard title="Alertes Actives" value="23" change="-5.1%" icon={AlertTriangle} trend="down" />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <RecentTransactions />
        <PendingKYC />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <AlertsWidget />
      </div>
    </>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <Layout>
      <DashboardContent />
    </Layout>
  );
};

export default App;
